// src/api/http.js
import axios from 'axios'

// Базовый URL API: .env или по умолчанию /api/
const raw = import.meta.env.VITE_API_BASE_URL || '/api/'
const baseURL = raw.endsWith('/') ? raw : raw + '/'

const axiosInstance = axios.create({
    baseURL,
    withCredentials: false, // JWT через Bearer — куки не нужны
    timeout: 25000,
})

// REQUEST: подставляем актуальный access‑токен
axiosInstance.interceptors.request.use((config) => {
    try {
        // Берём из localStorage, который синхронизирует стор auth
        const access = typeof window !== 'undefined' ? window.localStorage.getItem('auth_access') : null
        if (access) {
            config.headers = config.headers || {}
            config.headers.Authorization = `Bearer ${access}`
        }
    } catch (_) {
        // noop
    }
    return config
})

// RESPONSE: авто‑рефреш по 401 однажды и повтор исходного запроса
let refreshPromise = null

// Проверка на невалидный токен в теле ответа (Django REST Framework JWT)
function isTokenInvalidError(response) {
    if (!response) return false
    const data = response.data
    if (!data) return false

    // Проверяем типичные коды ошибок JWT от Django
    const invalidCodes = ['token_not_valid', 'token_expired', 'invalid_token']
    if (data.code && invalidCodes.includes(data.code)) return true

    // Проверяем detail с типичными сообщениями
    const detail = data.detail || ''
    if (typeof detail === 'string' && (
        detail.includes('token not valid') ||
        detail.includes('token expired') ||
        detail.includes('invalid token')
    )) return true

    return false
}

axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const { response, config } = error || {}
        if (!response) return Promise.reject(error)

        // Проверяем 401 или явную ошибку невалидного токена
        const is401 = response.status === 401
        const isInvalidToken = isTokenInvalidError(response)

        if ((is401 || isInvalidToken) && config && !config._retry) {
            // ВАЖНО: Если это сам refresh endpoint упал с 401 - НЕ пытаемся его refresh-ить
            // Это приведет к бесконечному циклу
            const isRefreshEndpoint = config.url?.includes('/auth/jwt/refresh')

            if (isRefreshEndpoint) {
                // Refresh token невалиден - сразу logout и редирект
                refreshPromise = null
                try {
                    const { useAuthStore } = await import('@/stores/auth.js')
                    const auth = useAuthStore()
                    await auth.logout()
                } catch (_) {}

                if (typeof window !== 'undefined') {
                    try {
                        const router = await import('@/router/index.js').then(m => m.default)
                        await router.replace('/login')
                    } catch (_) {
                        window.location.replace('/login')
                    }
                }
                return Promise.reject(error)
            }

            // Для всех остальных запросов пытаемся refresh один раз
            config._retry = true
            try {
                if (!refreshPromise) {
                    // Ленивая загрузка стора, чтобы избежать циклических зависимостей
                    const { useAuthStore } = await import('@/stores/auth.js')
                    const auth = useAuthStore()
                    refreshPromise = auth.refreshAccess() // обновит access и положит его в localStorage
                }
                await refreshPromise
                refreshPromise = null
                // Повторяем исходный запрос с новым токеном
                return axiosInstance(config)
            } catch (e) {
                refreshPromise = null
                // Рефреш не удался — чистим авторизацию и уводим на /login
                try {
                    const { useAuthStore } = await import('@/stores/auth.js')
                    const auth = useAuthStore()
                    await auth.logout()
                } catch (_) {}

                // Используем router для редиректа вместо window.location
                if (typeof window !== 'undefined') {
                    try {
                        const router = await import('@/router/index.js').then(m => m.default)
                        const current = window.location.pathname + window.location.search + window.location.hash
                        await router.replace({
                            path: '/login',
                            query: current !== '/login' ? { redirect: current } : {}
                        })
                    } catch (_) {
                        // Fallback если router недоступен
                        window.location.replace('/login')
                    }
                }
                return Promise.reject(e || error)
            }
        }

        return Promise.reject(error)
    }
)

export default axiosInstance


